import { NgClass } from '@angular/common';
import { Component, effect, inject, Input, input, signal, viewChild } from '@angular/core';
import { RouterLink } from '@angular/router';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { ECElementEvent, EChartsOption, PieSeriesOption, SeriesOption } from 'echarts';
import { TooltipDirective } from 'ngx-bootstrap/tooltip';

import { StringTMap } from '@bn/primitives';

import { CardComponent } from '../card/card.component';
import { ChartComponent } from '../chart/components/base/chart/chart.component';
import { ChartType } from '../chart/enums/chart-type.enum';
import { EChartsInstance } from '../chart/models/echarts-instance.model';
import { LazyLoadDirective } from '../directives/lazy-load.directive';
import { ThemeService } from '../theme/services/theme.service';

import { DoubleSection, ItemListSelection, ItemListSelectionLink, Section, SectionBase } from './models';
import { VisibleItemsPipe } from './pipes/item-list-visible-items.pipe';

@Component({
  selector: 'bn-web-ui-item-list-selection',
  templateUrl: './item-list-selection.component.html',
  styleUrls: ['./item-list-selection.component.scss'],
  imports: [CardComponent, NgClass, RouterLink, FaIconComponent, LazyLoadDirective, TooltipDirective, ChartComponent, VisibleItemsPipe],
})
export class ItemListSelectionComponent {
  readonly #themeService = inject(ThemeService);

  public readonly chartComponent = viewChild.required<ChartComponent>('chartComponent');

  @Input()
  public heading = '';

  @Input()
  public isLoading = false;

  readonly link = input<ItemListSelectionLink>();

  @Input()
  public set data(value: ItemListSelection | undefined) {
    this.list = this.sortBySortValue(value);
    this.selectedItemIndex = this.findFirstVisibleIndex();
    this.updateChartData();
  }

  public list?: ItemListSelection;
  public selectedItemIndex = 0;
  public selectedSubItemIndex = signal<number | null>(null);
  public chartData: StringTMap<string | number>[] = [];
  public chartOptions: EChartsOption = {
    legend: {
      show: false,
    },
    toolbox: {
      show: false,
    },
    tooltip: {
      valueFormatter: () => '', // Note: eliminate numbers/amounts from tooltips
    },
    series: [
      {
        type: 'pie',
        label: {
          show: false,
        },
        radius: ['40%', '70%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 5,
          borderWidth: 2,
          borderColor: getComputedStyle(document.documentElement).getPropertyValue('--color-base'),
        },
        data: [],
      },
    ],
  };

  public readonly chartTypes = ChartType;

  get seriesArray(): { data: StringTMap<number | string>[] }[] {
    const s = this.chartOptions?.series;
    const nonArray = s && !Array.isArray(s) ? [s] : (s ?? []);
    const arr = Array.isArray(s) ? s : nonArray;

    return arr.map((item) => ({
      ...item,
      data: Array.isArray(item.data) ? item.data : [],
    }));
  }

  // Note: false when the selected item has neither visible sub-items nor chart data — the card then collapses to a list-only layout instead of rendering a blank right panel.
  get hasRightPanelContent(): boolean {
    const selected = this.list?.[this.selectedItemIndex];

    if (!selected || !this.isSectionBase(selected)) return false;

    const hasVisibleSubItems = !!selected.items?.some((item) => !item.hide);
    const hasChartData = Array.isArray(selected.chartData) && selected.chartData.length > 0;

    return hasVisibleSubItems || hasChartData;
  }

  constructor() {
    effect(() => {
      this.#themeService.getCurrentTheme();

      if (this.chartOptions && Array.isArray(this.chartOptions.series) && this.chartOptions.series[0]) {
        const seriesItem = this.chartOptions.series[0] as PieSeriesOption;

        if (!seriesItem.itemStyle) {
          seriesItem.itemStyle = {};
        }
        seriesItem.itemStyle.borderColor = getComputedStyle(document.documentElement).getPropertyValue('--color-base');
      }
    });
  }

  public onChartReady(chartInstance: EChartsInstance): void {
    const selectedSubItemIndex = this.selectedSubItemIndex;

    chartInstance.on('mouseover', (params: ECElementEvent) => {
      selectedSubItemIndex.set(params.dataIndex);
    });

    chartInstance.on('mouseout', () => {
      selectedSubItemIndex.set(null);
    });
  }

  public handleItemClick(index: number): void {
    if (this.list?.[index].notSelectable) return;

    this.selectedItemIndex = index;

    this.updateChartData();
  }

  public updateChartData(): void {
    const currentSeries: PieSeriesOption =
      Array.isArray(this.chartOptions.series) && this.chartOptions.series.length > 0 ? (this.chartOptions.series[0] as PieSeriesOption) : { type: 'pie' };

    this.chartOptions = {
      ...this.chartOptions,
      series: [
        {
          ...currentSeries,
          type: 'pie',
          data: this.list?.[this.selectedItemIndex]?.chartData ?? [],
        },
      ],
    };
  }

  private sortBySortValue(value: ItemListSelection | undefined): ItemListSelection | undefined {
    if (!value?.some((section) => section.sortValue !== undefined)) return value;

    const sortable = value.filter((section) => section.sortValue !== undefined);
    const rest = value.filter((section) => section.sortValue === undefined);

    sortable.sort((a, b) => (b.sortValue ?? 0) - (a.sortValue ?? 0));

    return [...sortable, ...rest];
  }

  // Note: default-select the first visible, selectable product even when it has no chart data — its sub-item list still shows. Returns -1 when nothing is selectable so no row is highlighted.
  private findFirstVisibleIndex(): number {
    if (!this.list) return 0;

    return this.list.findIndex((item) => !item.hide && !item.notSelectable);
  }

  public isSectionBase(item: Section): item is SectionBase {
    return !('isDouble' in item);
  }

  public isDoubleSection(item: Section): item is DoubleSection {
    return 'isDouble' in item;
  }

  public hoverSlice(seriesIndex = 0, dataIndex: number): void {
    const chartComponent = this.chartComponent();
    if (!chartComponent) return;

    chartComponent.dispatchAction({ type: 'highlight', seriesIndex, dataIndex });
    chartComponent.dispatchAction({ type: 'showTip', seriesIndex, dataIndex });
  }

  public hoverSliceByName(name: string, seriesIndex = 0): void {
    const series = this.chartOptions.series as SeriesOption[];
    const data = series[seriesIndex].data as { name: string }[];
    const idx = data.findIndex((d) => d.name === name);

    if (idx >= 0) this.hoverSlice(seriesIndex, idx);
  }

  public clearHover(seriesIndex = 0): void {
    const chartComponent = this.chartComponent();
    if (!chartComponent) return;

    chartComponent.dispatchAction({ type: 'downplay', seriesIndex });
    chartComponent.dispatchAction({ type: 'hideTip' });
  }
}
